import {
  ClockIcon,
  FilmIcon,
  FingerprintIcon,
  ImagesIcon,
  InfoIcon,
  UsersIcon,
} from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import { EnrichmentStatusBadge } from "@/components/common/enrichment-status-badge";
import { EnrichmentSummary } from "@/components/enrichment/enrichment-summary";
import { ShowViewHeader } from "@/components/refine-ui/views/show-view";
import {
  CastList,
  Chips,
  DetailField,
  DetailGrid,
  DetailSection,
  MediaGallery,
  MetaDot,
  RawJsonCard,
  ShowHero,
  SystemSection,
  fmtDateTime,
  useShowRecord,
} from "@/components/show";
import { Badge } from "@/components/ui/badge";
import { sortedEntries } from "@/pages/movies/lib/country-map";
import {
  MovieViewType,
  movieViewSchema,
} from "@/pages/movies/schemas/movie.view";
import { EntityTinaTrace } from "@/pages/tina/components/entity-tina-trace";
import { ReEnrichButton } from "@/pages/tina/components/re-enrich-button";

/** "GR Κ-12" chips, sorted by country code. */
function countryChips(map?: Record<string, string> | null): string[] {
  return sortedEntries(map).map(([country, value]) => `${country} ${value}`);
}

function formatDuration(minutes?: number | null): string | undefined {
  if (!minutes || minutes <= 0) return undefined;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h ? `${h}h` : null, m ? `${m}m` : null].filter(Boolean).join(" ");
}

function releaseYear(raw?: string | number | null): string | undefined {
  if (typeof raw === "string" && raw.length >= 4) return raw.slice(0, 4);
  if (typeof raw === "number")
    return String(new Date(raw * 1000).getUTCFullYear());
  return undefined;
}

export const MoviesShow = () => {
  const { record, raw } = useShowRecord<MovieViewType>(
    movieViewSchema,
    (r) => `Movie | ${r.title ?? r.id}`,
  );

  return (
    <div className="p-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <ShowViewHeader />

        {record && (
          <>
            <ShowHero
              poster={record.media?.photos?.[0]}
              eyebrow={[
                releaseYear(record.releaseDate),
                record.originalLanguage,
              ]
                .filter(Boolean)
                .join(" · ")}
              title={record.title ?? "Untitled"}
              subtitle={
                record.originalTitle && record.originalTitle !== record.title
                  ? record.originalTitle
                  : undefined
              }
              meta={
                <>
                  {formatDuration(record.durationInMinutes) && (
                    <span className="inline-flex items-center gap-1">
                      <ClockIcon className="size-3.5" />
                      {formatDuration(record.durationInMinutes)}
                    </span>
                  )}
                  {typeof record.popularity === "number" && (
                    <>
                      <MetaDot />
                      <span>Popularity {record.popularity.toFixed(1)}</span>
                    </>
                  )}
                  {typeof record.interestedIn === "number" && (
                    <>
                      <MetaDot />
                      <span>{record.interestedIn} interested</span>
                    </>
                  )}
                </>
              }
              badges={
                <>
                  {record.status && (
                    <Badge variant="default">{record.status}</Badge>
                  )}
                  {/* Shared badge, not a local one: the /movies list column
                      already routes through it, so a hand-rolled copy here made
                      the same row read "Needs Review" in the list and
                      "NeedsReview" one click later. It renders nothing when the
                      status is absent, so it needs no guard. */}
                  <EnrichmentStatusBadge status={record.enrichmentStatus} />
                  {record.adult && <Badge variant="destructive">Adult</Badge>}
                  {record.hide && <Badge variant="secondary">Hidden</Badge>}
                  {(record.genres ?? []).map((g) => (
                    <Badge key={g.id ?? g.name} variant="secondary">
                      {g.name}
                    </Badge>
                  ))}
                </>
              }
              description={record.overview}
            />

            <DetailSection
              title="Cast & Crew"
              icon={<UsersIcon className="size-4" />}
            >
              <CastList cast={record.cast} />
            </DetailSection>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <DetailSection
                title="Details"
                icon={<FilmIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="Title" value={record.title} />
                  <DetailField
                    label="Original title"
                    value={record.originalTitle}
                  />
                  <DetailField
                    label="Original language"
                    value={record.originalLanguage}
                  />
                  <DetailField
                    label="Release date"
                    value={fmtDateTime(record.releaseDate)}
                  />
                  <DetailField
                    label="Duration"
                    value={formatDuration(record.durationInMinutes)}
                  />
                  <DetailField label="Slug" value={record.slug} mono />
                  <DetailField label="Genres" full>
                    <Chips items={(record.genres ?? []).map((g) => g.name)} />
                  </DetailField>
                  <DetailField label="Tags" full>
                    <Chips items={record.tags} variant="outline" />
                  </DetailField>
                  <DetailField label="Age ratings" full>
                    <Chips items={countryChips(record.certifications)} />
                  </DetailField>
                  <DetailField label="Release dates by country" full>
                    <Chips
                      items={countryChips(record.releaseDates)}
                      variant="outline"
                    />
                  </DetailField>
                </DetailGrid>
              </DetailSection>

              <DetailSection
                title="Identifiers"
                icon={<FingerprintIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="ID" mono>
                    <span className="inline-flex items-center gap-1">
                      {record.id}
                      <CopyButton text={record.id} message="Copied movie ID" />
                    </span>
                  </DetailField>
                  <DetailField label="Source" value={record.metadata?.source} />
                  <DetailField
                    label="TMDB ID"
                    value={record.metadata?.tmdbId?.toString()}
                    mono
                  />
                  <DetailField
                    label="IMDb ID"
                    value={record.metadata?.imdbId}
                    mono
                  />
                  <DetailField
                    label="WikiData ID"
                    value={record.metadata?.wikiDataId}
                    mono
                  />
                </DetailGrid>
              </DetailSection>
            </div>

            <EnrichmentSummary
              resource="movies"
              id={record.id}
              status={record.enrichmentStatus}
              enrichment={record.enrichment}
              bordered
            />

            {(record.trailers?.length ?? 0) > 0 && (
              <DetailSection
                title="Trailers"
                icon={<InfoIcon className="size-4" />}
              >
                <ul className="space-y-2">
                  {record.trailers!.map((t, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between gap-3 rounded-lg border p-2 text-sm"
                    >
                      <a
                        href={t.url ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        className="truncate text-primary hover:underline"
                      >
                        {t.url}
                      </a>
                      <div className="flex shrink-0 items-center gap-1.5">
                        {t.language && (
                          <Badge variant="outline">{t.language}</Badge>
                        )}
                        {t.subtitles && (
                          <Badge variant="secondary">CC {t.subtitles}</Badge>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </DetailSection>
            )}

            <DetailSection
              title="Media"
              icon={<ImagesIcon className="size-4" />}
            >
              <MediaGallery media={record.media} aspect="portrait" />
            </DetailSection>

            {/* Tina — provenance, and the only non-SQL way to re-queue this
                entity's enrichment. Unlike
                artist/venue/geodata/event this entity has no per-entity enrich
                trigger anywhere in IMS, so `POST /enrichments` is not a second
                path to the same thing — it is the only one. */}
            <div className="flex flex-col gap-4">
              <div className="flex justify-end">
                <ReEnrichButton entityType="movie" entityId={record.id} />
              </div>
              <EntityTinaTrace
                entityType="movie"
                entityId={record.id}
                metadata={record.metadata}
                enrichment={record.enrichment}
              />
            </div>

            <SystemSection record={record} />

            <RawJsonCard data={raw} />
          </>
        )}
      </div>
    </div>
  );
};
